import { connect, useDispatch } from 'react-redux';
import { useCallback, useMemo, useRef } from 'react';
import { Dispatch } from 'redux';
import { Form, FormRenderProps } from 'react-final-form';
import { FormApi } from 'final-form';

import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { KindResultDecisionCodesEnum } from 'APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';
import { KnmResultDecisionStatusEnum } from 'APIPackage/KnmModule/enums/KnmResultDecisionStatusEnum';
import { IKnmAttachmentDocument } from 'KnmPackage/KnmDocumentsModule/interfaces/IKnmAttachmentDocument';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { coreSetLoading } from 'CorePackage/MainModule/actions/coreActions';
import { fetchErknmActCancel } from 'APIPackage/KnmModule/services/knmApi';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getKnmErknmPositionsSelectOptions,
  getKnmFormRef,
  getKnmId,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { knmReload, knmRestoreToLatestVersion, knmTryOpenSignModal } from 'KnmPackage/MainModule/actions/knmActions';
import { IKnmErknmFormResultDecision } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormResultDecision';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IDictionaryPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { IDictionaryParticipantPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryParticipantPositionSelectOption';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { Hint } from 'CorePackage/MainModule/components/Hint';
import { AgreementStatusEnum } from 'APIPackage/KnmModule/enums/AgreementStatusEnum';

import styles from './ErknmActCancelModal.module.scss';
import { ErknmActCancelModalDocument } from './ErknmActCancelModalDocument';
import { FieldMark } from '../FieldMark/FieldMark';

interface IErknmActCancelModalProps {
  visible: boolean;
  onClose: () => void;
  isPm: boolean;
}

interface IErknmActCancelModalStoreProps {
  knmId: number | undefined;
  formRef: IKnmFormRef | undefined;
  positionsSelectOptions: IDictionaryPositionSelectOption[] | IDictionaryParticipantPositionSelectOption[] | undefined;
}

interface IForm {
  selectedAct: ISelectOption<string> | undefined;
  comment: string;
  attachment: IKnmAttachmentDocument | undefined;
  officialFullNameCancel: string;
  resultContentPositionCancel: IDictionaryPositionSelectOption | IDictionaryParticipantPositionSelectOption | undefined;
}

const ErknmActCancelModalWithoutStore = ({
  visible,
  onClose,
  knmId,
  formRef,
  isPm,
  positionsSelectOptions,
}: IErknmActCancelModalProps & IErknmActCancelModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const formRefInner = useRef<FormApi<IForm, Partial<IForm>>>();
  const valuesParentForm = formRef?.current.getState().values;

  const validateForm = useCallback((values: IForm) => {
    return {
      selectedAct: makeRequired('Поле не может быть пустым')(values.selectedAct),
      comment: makeRequired('Поле не может быть пустым')(values.comment),
      attachment: makeRequired('Поле не может быть пустым')(values.attachment),
      officialFullNameCancel: makeRequired('Поле не может быть пустым')(values.officialFullNameCancel),
      resultContentPositionCancel: makeRequired('Поле не может быть пустым')(values.resultContentPositionCancel),
    };
  }, []);

  const handleClose = () => {
    onClose();
    formRefInner?.current?.reset();
  };

  const actOptions = useMemo(() => {
    const prescribes: IKnmErknmFormResultDecision[] = [];

    valuesParentForm?.resultDecisions?.forEach(decision => {
      if (
        (decision.kindDecision?.code === KindResultDecisionCodesEnum.D_1 ||
          decision.kindDecision?.code === KindResultDecisionCodesEnum.D_7) &&
        (decision.status === KnmResultDecisionStatusEnum.COMPLETED ||
          decision.status === KnmResultDecisionStatusEnum.NOT_COMPLETED) &&
        decision.signed
      ) {
        prescribes.push(decision);
      }
    });

    const acts: ISelectOption<string>[] = [];
    const agreements = valuesParentForm?.agreements;

    if (isPm) {
      if (valuesParentForm?.act?.signed && valuesParentForm?.act.guid && !valuesParentForm?.act.fileInvalidate) {
        const prescribe = valuesParentForm?.pmResultDecisions?.find(decision => {
          return (
            (decision.kindDecision?.code === KindResultDecisionCodesEnum.D_1 ||
              decision.kindDecision?.code === KindResultDecisionCodesEnum.D_7) &&
            (decision.status === KnmResultDecisionStatusEnum.COMPLETED ||
              decision.status === KnmResultDecisionStatusEnum.NOT_COMPLETED) &&
            decision.signed
          );
        });

        if (prescribe) {
          const bindedAgreement = agreements?.find(
            agreement =>
              agreement.prescription?.value === prescribe.guid &&
              (agreement.status === AgreementStatusEnum.DRAFT ||
                agreement.status === AgreementStatusEnum.READY_TO_APPROVE ||
                agreement.status === AgreementStatusEnum.ON_APPROVAL ||
                agreement.status === AgreementStatusEnum.APPROVED ||
                agreement.status === AgreementStatusEnum.PENDING_TERMINATION ||
                agreement.status === AgreementStatusEnum.COMPLETED),
          );

          if (!bindedAgreement) {
            acts.push({
              id: valuesParentForm?.act?.guid,
              label: `Акт - №${valuesParentForm?.act?.numberAct}`,
              value: { aGuid: valuesParentForm?.act?.guid, pGuid: prescribe?.guid },
            });
          }
        } else {
          acts.push({
            id: valuesParentForm?.act?.guid,
            label: `Акт - №${valuesParentForm?.act?.numberAct}`,
            value: { aGuid: valuesParentForm?.act?.guid },
          });
        }
      }
    } else {
      valuesParentForm?.acts?.forEach(act => {
        if (act.signed && act.guid && !act.fileInvalidate) {
          const prescribe = prescribes.find(p => p.act?.id === act.guid);

          if (prescribe) {
            const bindedAgreement = agreements?.find(
              agreement =>
                agreement.prescription?.value === prescribe.guid &&
                (agreement.status === AgreementStatusEnum.DRAFT ||
                  agreement.status === AgreementStatusEnum.READY_TO_APPROVE ||
                  agreement.status === AgreementStatusEnum.ON_APPROVAL ||
                  agreement.status === AgreementStatusEnum.APPROVED ||
                  agreement.status === AgreementStatusEnum.PENDING_TERMINATION ||
                  agreement.status === AgreementStatusEnum.COMPLETED),
            );

            if (!bindedAgreement) {
              acts.push({
                id: act.guid,
                label: `Акт - №${act.numberAct}`,
                value: { aGuid: act.guid, pGuid: prescribe?.guid },
              });
            }
          } else {
            acts.push({
              id: act.guid,
              label: `Акт - №${act.numberAct}`,
              value: { aGuid: act.guid },
            });
          }
        }
      });
    }

    return acts;
  }, [valuesParentForm]);

  const handleCancel = useCallback(
    async (values: IForm) => {
      const data = {
        knmId,
        ...values,
        prescriptionGuid: values.selectedAct?.value?.pGuid,
        actGuid: values.selectedAct?.value?.aGuid,
        resultContentPositionCancel: values.resultContentPositionCancel?.value,
      };

      const actCancel = async () => {
        dispatch(coreSetLoading(true));

        await fetchErknmActCancel(data);

        dispatch(coreSetLoading(false));
        dispatch(knmReload());
      };

      handleClose();
      dispatch(
        knmTryOpenSignModal(data.actGuid, undefined, true, actCancel, () => dispatch(knmRestoreToLatestVersion())),
      );
    },
    [knmId, handleClose],
  );

  const renderContentForm = useCallback(
    ({ handleSubmit, form, values }: FormRenderProps<IForm>) => {
      formRefInner.current = form;

      return (
        <>
          <ModalBody size="content">
            <div className={styles.ContentWrapper}>
              <FieldBlock
                label={<FieldMark fieldName="selectedAct" label="Номер акта" />}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <SelectInputField
                  name="selectedAct"
                  className={styles.SelectInput}
                  options={actOptions}
                  isClearable={false}
                />
              </FieldBlock>
              <FieldBlock
                label={<FieldMark fieldName="comment" label="Комментарий" />}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextareaField
                  name={`comment`}
                  size="medium"
                  resize="vertical"
                  inputClassName={styles.CopyReasonTextarea}
                  autoHeight={true}
                />
              </FieldBlock>
              <ErknmActCancelModalDocument
                values={values.attachment ? [values.attachment] : []}
                addAttachment={data => form.change('attachment', data)}
                removeField={() => form.change('attachment')}
              />
              <Row>
                <FieldBlock
                  label={<FieldMark fieldName="officialFullNameCancel" label="ФИО должностного лица" />}
                  color="black"
                  size="half"
                >
                  <TextInputField name={`officialFullNameCancel`} />
                </FieldBlock>
                <FieldBlock
                  label={
                    <FieldMark
                      fieldName="resultContentPositionCancel"
                      label="Должность лица, отменившего предостережение"
                    />
                  }
                  color="black"
                  size="half"
                >
                  <div className={styles.PositionField}>
                    <SelectInputField
                      name={`resultContentPositionCancel`}
                      placeholder="Выберите должность"
                      options={positionsSelectOptions}
                      isSearchable
                      size="medium"
                      opensOnTop
                    />
                    <Hint text="Должность лица уполномоченного на принятие решения (из ЕРВК)" />
                  </div>
                </FieldBlock>
              </Row>
            </div>
          </ModalBody>
          <ModalActions>
            <Button text="Отменить" size="medium" type="button" onClick={handleClose} />
            <Button text="Сохранить" color="primary" size="medium" type="button" onClick={handleSubmit} />
          </ModalActions>
        </>
      );
    },
    [positionsSelectOptions, handleClose],
  );

  return (
    <Modal visible={visible} onClose={handleClose}>
      <ModalHeader title="Отмена результатов" />
      <Form<IForm> render={renderContentForm} onSubmit={handleCancel} validate={validateForm} />
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): IErknmActCancelModalStoreProps => ({
  knmId: getKnmId(state),
  formRef: getKnmFormRef(state),
  positionsSelectOptions: getKnmErknmPositionsSelectOptions(state),
});

export const ErknmActCancelModal = connect(mapStateToProps)(ErknmActCancelModalWithoutStore);
